import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { ClassNavigation } from '../components/layout';

interface ClassInfo {
  id: string;
  name: string;
  code: string;
  term: string;
}

interface Student {
  id: string;
  name: string;
  email: string;
  enrollmentDate: string;
  lastActive: string;
  engagementScore: number;
  profileCompleted: boolean;
  profileProgress: number; // 0-17 steps
  surveyCompleted: boolean;
  surveyProgress: number; // 0-114 questions
  surveyCompletionPercentage: number; // 0-100%
  status: 'active' | 'inactive' | 'at-risk';
  auraProfile: {
    dominant: string;
    secondary: string;
    color: string;
  };
}

// Helper functions for calculating metrics from student responses
const calculateEngagementScore = (responses: any): number => {
  if (!responses || typeof responses !== 'object') return 0;
  
  // Simple calculation based on completion and participation indicators
  const completed = Object.keys(responses).length;
  const maxQuestions = 17; // Based on setup store
  const completionRate = (completed / maxQuestions) * 100;
  
  // Add some variability based on response patterns
  const participationBonus = responses.participation_level ? 
    (responses.participation_level === 'high' ? 20 : 
     responses.participation_level === 'medium' ? 10 : 0) : 0;
  
  return Math.min(100, Math.round(completionRate + participationBonus));
};

const generateAuraProfile = (responses: any): { dominant: string; secondary: string; color: string } => {
  if (!responses || typeof responses !== 'object') {
    return { dominant: 'Unknown', secondary: 'Unknown', color: '#6b7280' };
  }
  
  // Analyze responses to determine learning style
  const learningStyles = {
    analytical: 0,
    visual: 0,
    kinesthetic: 0,
    social: 0,
    reflective: 0
  };
  
  // Score based on various response patterns
  if (responses.learning_preference === 'structured') learningStyles.analytical += 3;
  if (responses.learning_preference === 'visual') learningStyles.visual += 3;
  if (responses.learning_preference === 'hands-on') learningStyles.kinesthetic += 3;
  if (responses.communication_style === 'collaborative') learningStyles.social += 2;
  if (responses.feedback_preference === 'detailed') learningStyles.reflective += 2;
  
  // Find dominant and secondary styles
  const sorted = Object.entries(learningStyles).sort(([,a], [,b]) => b - a);
  const dominant = sorted[0][0];
  const secondary = sorted[1][0];
  
  // Map to display names and colors
  const styleMap: Record<string, { name: string; color: string }> = {
    analytical: { name: 'Analytical Learner', color: '#10b981' },
    visual: { name: 'Visual Learner', color: '#3b82f6' },
    kinesthetic: { name: 'Kinesthetic Learner', color: '#8b5cf6' },
    social: { name: 'Social Learner', color: '#ef4444' },
    reflective: { name: 'Reflective Learner', color: '#f59e0b' }
  };
  
  return {
    dominant: styleMap[dominant]?.name || 'Adaptive Learner',
    secondary: styleMap[secondary]?.name || 'Flexible',
    color: styleMap[dominant]?.color || '#6b7280'
  };
};

const Students = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState('');
  const [statusFilter] = useState<'all' | 'active' | 'inactive' | 'at-risk'>('all');
  const [surveyFilter] = useState<'all' | 'survey-completed' | 'profile-completed' | 'in-progress' | 'not-started'>('all');

  useEffect(() => {
    const loadStudentData = async () => {
      if (!classId || !user) return;

      setLoading(true);
      try {
        // Load class basic info
        const { data: classData } = await apiService.getClassById(classId);
        
        if (classData) {
          setClassInfo({
            id: classData.id,
            name: classData.name,
            code: classData.code,
            term: classData.term,
          });

          // Load students enrolled in this class
          const { data: studentsData, error: studentsError } = await apiService.getClassStudents(classId);
          
          if (studentsError) {
            console.error('Error loading students:', studentsError);
            return;
          }
          
          let formattedStudents: Student[] = [];
          
          if (studentsData) {
            // Load student profiles and survey data for each enrolled student
            for (const enrollment of studentsData) {
              const student = enrollment.student;
              if (!student) continue;
              
              // Get student profile data (class-based profile system)
              const { data: profileData } = await apiService.getStudentProfile(student.id, classId);
              
              // Get survey results (backend survey system)
              const { data: surveyResults, error: surveyError } = await apiService.getStudentSurveyResults(student.id);
              if (surveyError) {
                console.log(`Survey results error for student ${student.id}:`, surveyError);
              }
              
              // Calculate engagement score from profile responses
              const engagementScore = profileData?.responses ? 
                calculateEngagementScore(profileData.responses) : 0;
              
              // Profile completion status
              const profileCompleted = profileData?.completed || false;
              const profileProgress = profileData?.currentStep || 0;
              
              // Survey completion status
              const surveyCompleted = surveyResults?.hasResults && surveyResults?.completionPercentage >= 100;
              const surveyProgress = surveyResults?.totalResponses || 0;
              const surveyCompletionPercentage = surveyResults?.completionPercentage || 0;
              
              // Determine overall status based on both systems
              let status: 'active' | 'inactive' | 'at-risk';
              if (surveyCompleted && profileCompleted) {
                status = 'active';
              } else if (surveyProgress > 0 || profileProgress > 0) {
                // Has started either survey
                status = engagementScore < 30 ? 'at-risk' : 'inactive';
              } else {
                status = 'inactive'; // No activity yet
              }
              
              // Generate aura profile from responses
              const auraProfile = profileData?.responses ? 
                generateAuraProfile(profileData.responses) : {
                  dominant: 'New Student',
                  secondary: 'Profile Pending',
                  color: '#6b7280'
                };
              
              formattedStudents.push({
                id: student.id,
                name: student.full_name || 'Student',
                email: student.email,
                enrollmentDate: enrollment.enrollment_date ? new Date(enrollment.enrollment_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                lastActive: profileData?.updatedAt ? new Date(profileData.updatedAt).toISOString().split('T')[0] : (enrollment.enrollment_date ? new Date(enrollment.enrollment_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
                engagementScore,
                profileCompleted,
                profileProgress,
                surveyCompleted,
                surveyProgress,
                surveyCompletionPercentage,
                status,
                auraProfile
              });
            }
          }
          
          setStudents(formattedStudents);
        }
      } catch (error) {
        console.error('Failed to load student data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStudentData();
  }, [classId, user]);

  if (loading) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900">Class not found</h2>
            <p className="text-gray-600 mt-2">Unable to load students.</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'at-risk': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    // Search filter
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    
    // Survey completion filter
    let matchesSurvey = true;
    if (surveyFilter === 'survey-completed') {
      matchesSurvey = student.surveyCompleted;
    } else if (surveyFilter === 'profile-completed') {
      matchesSurvey = student.profileCompleted;
    } else if (surveyFilter === 'in-progress') {
      matchesSurvey = (student.surveyProgress > 0 && !student.surveyCompleted) || 
                     (student.profileProgress > 0 && !student.profileCompleted);
    } else if (surveyFilter === 'not-started') {
      matchesSurvey = student.surveyProgress === 0 && student.profileProgress === 0;
    }
    
    return matchesSearch && matchesStatus && matchesSurvey;
  });

  return (
    <div>
      <ClassNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            👥 {classInfo.name} - Students
          </h1>
          <p className="text-gray-600">{classInfo.code} • {classInfo.term}</p>
        </div>

        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-emerald-600">{students.length}</div>
              <div className="text-sm text-gray-600">Total Students</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-green-600">
                {students.filter(s => s.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Active Students</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-yellow-600">
                {students.filter(s => s.profileCompleted).length}
              </div>
              <div className="text-sm text-gray-600">Profiles Complete</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">
                {students.filter(s => s.surveyCompleted).length}
              </div>
              <div className="text-sm text-gray-600">Detailed Surveys</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-red-600">
                {students.filter(s => s.status === 'at-risk').length}
              </div>
              <div className="text-sm text-gray-600">At-Risk Students</div>
            </div>
          </div>


          {/* Detailed Student List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Student Details & Progress
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Click on a student to view detailed analytics and feedback history
              </p>
            </div>

            {students.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="text-gray-400 text-6xl mb-4">👥</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Enrolled</h3>
                <p className="text-gray-600">
                  Students haven't enrolled in this class yet. Share the class access link with your students.
                </p>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Found</h3>
                <p className="text-gray-600">
                  No students match your current search criteria. Try adjusting your filters.
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {filteredStudents.map((student) => (
                  <div 
                    key={student.id}
                    className="px-6 py-6 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    onClick={() => navigate(`/dashboard/class/${classId}/student/${student.id}`)}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                      {/* Student Info */}
                      <div className="lg:col-span-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                              <span className="text-emerald-700 font-medium text-sm">
                                {student.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {student.name}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {student.email}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Enrollment Details */}
                      <div className="lg:col-span-2">
                        <div className="text-xs text-gray-500 mb-1">Enrolled</div>
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(student.enrollmentDate).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {Math.floor((Date.now() - new Date(student.enrollmentDate).getTime()) / (1000 * 60 * 60 * 24))} days ago
                        </div>
                      </div>

                      {/* Status & Engagement */}
                      <div className="lg:col-span-2">
                        <div className="flex flex-col space-y-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(student.status)} w-fit`}>
                            {student.status}
                          </span>
                          <div className="flex items-center">
                            <div className="w-12 bg-gray-200 rounded-full h-1.5 mr-2">
                              <div
                                className="bg-emerald-500 h-1.5 rounded-full"
                                style={{ width: `${student.engagementScore}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-600">{student.engagementScore}%</span>
                          </div>
                        </div>
                      </div>

                      {/* Survey Progress */}
                      <div className="lg:col-span-2">
                        <div className="text-xs text-gray-500 mb-1">Progress Status</div>
                        <div className="space-y-2">
                          {/* Profile Progress */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-600">Profile:</span>
                              {student.profileCompleted ? (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                  ✓ Complete
                                </span>
                              ) : student.profileProgress > 0 ? (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                                  {student.profileProgress}/17
                                </span>
                              ) : (
                                <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                                  Not Started
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Detailed Survey Progress */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-600">Survey:</span>
                              {student.surveyCompleted ? (
                                <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                                  ✓ Complete
                                </span>
                              ) : student.surveyProgress > 0 ? (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                  {Math.round(student.surveyCompletionPercentage)}%
                                </span>
                              ) : (
                                <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                                  Not Started
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Combined Progress Bar */}
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500" 
                                 style={{ 
                                   width: `${Math.max(
                                     student.profileCompleted ? 50 : (student.profileProgress / 17) * 50,
                                     student.surveyCompleted ? 100 : 50 + (student.surveyCompletionPercentage / 100) * 50
                                   )}%` 
                                 }}>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Learning Profile */}
                      <div className="lg:col-span-2">
                        <div className="text-xs text-gray-500 mb-1">Learning Aura</div>
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: student.auraProfile.color }}
                          ></div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {student.auraProfile.dominant}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {student.auraProfile.secondary}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Last Activity */}
                      <div className="lg:col-span-1">
                        <div className="text-xs text-gray-500 mb-1">Last Active</div>
                        <div className="text-sm text-gray-900">
                          {(() => {
                            const daysSince = Math.floor((Date.now() - new Date(student.lastActive).getTime()) / (1000 * 60 * 60 * 24));
                            return daysSince === 0 ? 'Today' : 
                                   daysSince === 1 ? '1 day ago' : 
                                   daysSince < 7 ? `${daysSince} days ago` :
                                   new Date(student.lastActive).toLocaleDateString();
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>


          {/* Quick Actions */}
          {filteredStudents.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button type="button" className="flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Student Data
                </button>
                <button type="button" className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Survey Reminder
                </button>
                <button type="button" className="flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Generate Report
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Students;